import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
	Box,
	Title,
	Text,
	Select,
	Button,
	Group,
	Input,
	Tabs,
	Table,
	Badge,
	Anchor,
	useMantineTheme,
} from '@mantine/core'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import { updateUserRole, getUserPositions, setUserDefaultPosition } from '../services/actions/userActions'
import { getOrgDetails, getOrgUsers } from '../services/actions/orgActions'

const EditUserPage = () => {
	const theme = useMantineTheme()
	const { userId, orgSlug } = useParams()
	const [loadedUser, setLoadedUser] = useState(null)
	const [role, setRole] = useState('')
	const [phone, setPhone] = useState('')
	const [error, setError] = useState('')
	const [positions, setPositions] = useState([])
	const [positionsLoading, setPositionsLoading] = useState(true)
	const [positionsError, setPositionsError] = useState('')
	const [savingPosition, setSavingPosition] = useState(null)
	const [savingUser, setSavingUser] = useState(false)
	const navigate = useNavigate()

	const orgDetails = useSelector((state) => state.org.orgDetails)
	const cachedOrgId = orgDetails?.slug === orgSlug ? orgDetails.id : null
	const target = useMemo(() => ({ userId, orgSlug, cachedOrgId }), [userId, orgSlug, cachedOrgId])
	const activeTarget = useRef(target)
	activeTarget.current = target
	// Hide obsolete data immediately, before navigation's effect resets the form.
	const user = loadedUser?.target === target ? loadedUser.user : null
	const managedOrgId = user ? loadedUser.orgId : null
	const isPhoneValid = !phone || isValidPhoneNumber(phone)

	useEffect(() => {
		let cancelled = false
		activeTarget.current = target
		setLoadedUser(null)
		setRole('')
		setPhone('')
		setError('')
		setPositions([])
		setPositionsLoading(true)
		setPositionsError('')
		setSavingPosition(null)
		setSavingUser(false)
		const fetchUser = async () => {
			try {
				const [users, managedOrg] = await Promise.all([
					getOrgUsers(target.orgSlug),
					target.cachedOrgId
						? Promise.resolve({ id: target.cachedOrgId, slug: target.orgSlug })
						: getOrgDetails(target.orgSlug),
				])
				if (cancelled || activeTarget.current !== target) return
				if (!managedOrg?.id || managedOrg.slug !== target.orgSlug) {
					setError('Failed to load organization')
					return
				}
				const selectedUser = users.find((u) => u.id === target.userId)

				if (!selectedUser) {
					setError('User not found')
					return
				}

				if (selectedUser.isActive === false) {
					setError('Reactivate this user before editing.')
					return
				}

				setLoadedUser({ user: selectedUser, orgId: managedOrg.id, target })
				setRole((selectedUser.role || '').toLowerCase())
				setPhone(String(selectedUser.phone || '').trim())
				try {
					const data = await getUserPositions(target.userId, managedOrg.id)
					if (!cancelled && activeTarget.current === target) setPositions(data)
				} catch (err) {
					if (!cancelled && activeTarget.current === target) {
						setPositionsError(err.message || 'Failed to fetch positions')
					}
				} finally {
					if (!cancelled && activeTarget.current === target) setPositionsLoading(false)
				}
			} catch (err) {
				if (cancelled || activeTarget.current !== target) return
				console.error('Failed to fetch user:', err)
				setError('Failed to fetch user')
			}
		}

		fetchUser()
		return () => {
			cancelled = true
			if (activeTarget.current === target) activeTarget.current = null
		}
	}, [target])

	const handleSetDefault = async (positionId) => {
		if (!user || !managedOrgId || positionsLoading || savingPosition !== null) return
		setSavingPosition(positionId)
		setPositionsError('')
		try {
			const result = await setUserDefaultPosition(user.id, positionId, managedOrgId)
			if (activeTarget.current !== target) return
			setPositions((current) => current.map((position) => ({
				...position,
				isDefault: position.experienceId === result.experienceId,
			})))
		} catch (err) {
			if (activeTarget.current === target) setPositionsError(err.message || 'Failed to set default position')
		} finally {
			if (activeTarget.current === target) setSavingPosition(null)
		}
	}

	const handleSave = async () => {
		if (!user || !managedOrgId || savingUser) return
		if (!isPhoneValid) {
			setError('Enter a valid phone number or clear the field.')
			return
		}

		setSavingUser(true)
		setError('')
		try {
			await updateUserRole(user.id, role, managedOrgId, { phone })
			if (activeTarget.current !== target) return
			navigate(`/${orgSlug}/admin/users`)
		} catch (err) {
			if (activeTarget.current !== target) return
			console.error('Failed to update user:', err)
			setError(err.message || 'Failed to update user')
		} finally {
			if (activeTarget.current === target) setSavingUser(false)
		}
	}

	if (error && !user) return <Text color='red'>{error}</Text>
	if (!user) return <Text>Loading...</Text>

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Edit User
			</Title>

			<Text>
				<strong>Name:</strong> {user.name}
			</Text>
			<Text mb='sm'>
				<strong>Email:</strong> {user.email}
			</Text>

			<Tabs defaultValue='details'>
				<Tabs.List>
					<Tabs.Tab value='details'>Details</Tabs.Tab>
					<Tabs.Tab value='positions'>Positions</Tabs.Tab>
				</Tabs.List>
				<Tabs.Panel value='details' pt='md'>
					{error && <Text color='red' mb='md'>{error}</Text>}
					<Select
						label='Role'
						value={role}
						onChange={setRole}
						data={[
							{ value: 'user', label: 'User' },
							{ value: 'admin', label: 'Admin' },
						]}
						mb='md'
					/>

					<Input.Wrapper
						label='Phone'
						error={phone && !isPhoneValid ? 'Enter a valid phone number' : null}
						mb='md'
					>
						<PhoneInput
							defaultCountry='US'
							international
							countryCallingCodeEditable={false}
							value={phone || undefined}
							onChange={(value) => setPhone(value || '')}
							className='phone-input'
							style={{
								'--phone-input-background':
									theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
								'--phone-input-border':
									theme.colorScheme === 'dark'
										? theme.colors.dark[4]
										: theme.colors.gray[4],
								'--phone-input-color':
									theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
								'--phone-input-color-scheme': theme.colorScheme,
							}}
						/>
					</Input.Wrapper>

					<Group mt='md'>
						<Button onClick={handleSave} loading={savingUser} disabled={!managedOrgId || savingUser}>Save</Button>
						<Button
							variant='light'
							color='gray'
							onClick={() => navigate(`/${orgSlug}/admin/users`)}
						>
							Cancel
						</Button>
					</Group>
				</Tabs.Panel>
				<Tabs.Panel value='positions' pt='md'>
					<Text color='dimmed' size='sm' mb='md'>
						The default selects the experience this user opens next time they sign in.
						To modify or remove a position, go to that experience’s Manage Staff page.
					</Text>
					{positionsError && <Text color='red' role='alert' mb='md'>{positionsError}</Text>}
					{positionsLoading ? <Text>Loading positions...</Text> : positions.length === 0 ? (
						!positionsError && <Text>No positions assigned in this organization.</Text>
					) : (
						<Box sx={{ overflowX: 'auto' }}>
							<Table striped highlightOnHover>
								<thead><tr>
									<th>Experience</th><th>Level</th><th>Position</th><th>Station</th><th>Default</th><th>Manage</th>
								</tr></thead>
								<tbody>{positions.map((position) => (
									<tr key={position.id}>
										<td>{position.experienceName}{position.experienceStatus !== 'active' && (
											<Text size='xs' color='dimmed'>{position.experienceStatus}</Text>
										)}</td>
										<td>{position.level === 'owner' ? 'Owner' : position.level === 'staff' ? 'Staff' : '-'}</td>
										<td>{position.positionName || '-'}</td>
										<td>{position.stationName || '-'}</td>
										<td>{position.isDefault ? <Badge>Default</Badge> : (
											<Button size='xs' variant='light'
												aria-label={`Set ${position.experienceName} as default`}
												loading={savingPosition === position.id}
												disabled={savingPosition !== null || position.experienceStatus !== 'active'}
												onClick={() => handleSetDefault(position.id)}>
												Set default
											</Button>
										)}</td>
										<td><Anchor component={Link}
											to={`/${orgSlug}/experiences/${position.experienceSlug}/manage/staff`}
											aria-label={`Manage staff for ${position.experienceName}`}>
											Manage Staff
										</Anchor></td>
									</tr>
								))}</tbody>
							</Table>
						</Box>
					)}
				</Tabs.Panel>
			</Tabs>
		</Box>
	)
}

export default EditUserPage
